// Static authoring-contract checks for guest source, run beside the compile scan by `doss byofe check`.
// These checks do not replace a standard linter (oxLint), but augment it for Doss-specific patterns.
// These checks are deliberately regex/scan-grade: cheap, low-false-positive signals.

export type GuestLintSeverity = 'error' | 'warning'

export type GuestLintFinding = {
  rule: string
  severity: GuestLintSeverity
  /** 1-based line of the first offending character. */
  line: number
  message: string
}

/** One offending site a rule found in the comment-blanked source. */
export interface GuestLintSite {
  /** Offset of the first offending character. */
  readonly index: number
  /** What was found (the literal, the keyword); leads the message, ahead of the rule summary. */
  readonly detail?: string
  /** Downgrade for an uncertain match; otherwise the rule's severity. */
  readonly severity?: GuestLintSeverity
}

/** One authoring-contract rule: how it is reported, how to comply, and how it is found. */
export interface GuestAuthoringRule {
  readonly rule: string
  readonly severity: GuestLintSeverity
  /** One sentence naming what is banned and what to write instead. */
  readonly summary: string
  /** Scan comment-blanked source (strings intact) and return every offending site. */
  readonly check: (code: string) => readonly GuestLintSite[]
}

/**
 * Blank out comments while preserving byte offsets and line structure, so
 * rule regexes neither fire on commented-out code nor report shifted lines.
 * String and template-literal contents are preserved (colors hide in style
 * strings — exactly what the rules must see); comment DETECTION respects
 * string boundaries so `'http://x'` is not treated as a comment.
 */
const blankComments = (source: string): string => {
  const out = source.split('')
  type ScanState = 'code' | 'line-comment' | 'block-comment' | 'single' | 'double' | 'template'
  let state: ScanState = 'code'
  for (let i = 0; i < source.length; i += 1) {
    const ch = source[i]
    const next = source[i + 1]
    if (state === 'code') {
      if (ch === '/' && next === '/') {
        state = 'line-comment'
        out[i] = ' '
      } else if (ch === '/' && next === '*') {
        state = 'block-comment'
        out[i] = ' '
      } else if (ch === "'") {
        state = 'single'
      } else if (ch === '"') {
        state = 'double'
      } else if (ch === '`') {
        state = 'template'
      }
    } else if (state === 'line-comment') {
      if (ch === '\n') {
        state = 'code'
      } else {
        out[i] = ' '
      }
    } else if (state === 'block-comment') {
      if (ch === '*' && next === '/') {
        out[i] = ' '
        out[i + 1] = ' '
        i += 1
        state = 'code'
      } else if (ch !== '\n') {
        out[i] = ' '
      }
    } else if (state === 'single') {
      if (ch === '\\') {
        i += 1
      } else if (ch === "'" || ch === '\n') {
        state = 'code'
      }
    } else if (state === 'double') {
      if (ch === '\\') {
        i += 1
      } else if (ch === '"' || ch === '\n') {
        state = 'code'
      }
    } else {
      // template: tolerate nested ${} loosely — a closing backtick ends it.
      if (ch === '\\') {
        i += 1
      } else if (ch === '`') {
        state = 'code'
      }
    }
  }
  return out.join('')
}

const lineOfOffset = (source: string, offset: number): number => {
  let line = 1
  for (let i = 0; i < offset && i < source.length; i += 1) {
    if (source[i] === '\n') {
      line += 1
    }
  }
  return line
}

/** Every match of `pattern`, as a site; `detail` names what was found. */
const sitesOf = (
  code: string,
  pattern: RegExp,
  detail?: (match: RegExpExecArray) => string,
): GuestLintSite[] =>
  [...code.matchAll(pattern)].map((match) =>
    detail === undefined ? { index: match.index } : { index: match.index, detail: detail(match) },
  )

// ---------------------------------------------------------------------------------------------
// Design tokens

// Color functions count only with a literal numeric first argument, so `rgba(${r}, …)` built
// from a resolved token passes.
const COLOR_LITERAL_PATTERN =
  /#(?:[0-9a-fA-F]{8}|[0-9a-fA-F]{6}|[0-9a-fA-F]{3,4})\b|\b(?:oklch|oklab|rgba?|hsla?)\(\s*[\d.]/g

const noLiteralColor: GuestAuthoringRule = {
  rule: 'no-literal-color',
  severity: 'error',
  summary:
    'No literal colors anywhere in guest source (#hex, rgb()/rgba(), hsl(), oklch()/oklab() — ' +
    'var() fallbacks and chart palettes included); resolve semantic tokens instead, e.g. ' +
    "resolveCssColor('--info-text') or var(--text)",
  check: (code) =>
    sitesOf(code, COLOR_LITERAL_PATTERN, (match) => {
      const label = match[0].startsWith('#') ? match[0] : `${match[0].split('(')[0] ?? match[0]}(…)`
      return `Literal color "${label}"`
    }),
}

const FONT_FAMILY_PATTERN = /(?:font-family\s*:|fontFamily\s*:)\s*([^;\n}]*)/g

const noFontFamilyLiteral: GuestAuthoringRule = {
  rule: 'no-font-family-literal',
  severity: 'error',
  summary: 'No literal font-family; use var(--font-family-body) or var(--font-family-mono)',
  check: (code) =>
    [...code.matchAll(FONT_FAMILY_PATTERN)]
      .filter((match) => !(match[1] ?? '').includes('var(--'))
      .map((match) => ({ index: match.index })),
}

// ---------------------------------------------------------------------------------------------
// SQL

/** Template literals and quoted strings that look like SQL statements. */
const SQL_STRING_PATTERN = /`[^`]*`|'[^'\n]*'|"[^"\n]*"/g

const sqlStatements = (code: string): RegExpExecArray[] =>
  [...code.matchAll(SQL_STRING_PATTERN)].filter((match) => /\bSELECT\b/i.test(match[0]))

const sqlLimitWithoutOrderBy: GuestAuthoringRule = {
  rule: 'sql-limit-without-order-by',
  severity: 'warning',
  summary:
    'A SQL LIMIT needs an ORDER BY with unique tie-breakers; without one the returned window ' +
    'is arbitrary',
  check: (code) =>
    sqlStatements(code)
      .filter((match) => /\bLIMIT\s+\d+/i.test(match[0]) && !/\bORDER\s+BY\b/i.test(match[0]))
      .map((match) => ({ index: match.index })),
}

// Rejected at execution with SQLSTATE 0A000: duplicate-preserving output has no row identity to
// page by.
const sqlNoSetOpAll: GuestAuthoringRule = {
  rule: 'sql-no-set-op-all',
  severity: 'error',
  summary:
    'UNION/INTERSECT/EXCEPT ALL is rejected by the SQL engine; use one useSql call per branch, ' +
    'or the distinct set operation for genuine set semantics',
  check: (code) =>
    sqlStatements(code).flatMap((match) => {
      const setOpAll = /\b(UNION|INTERSECT|EXCEPT)\s+ALL\b/i.exec(match[0])
      if (setOpAll === null) {
        return []
      }
      return [{ index: match.index, detail: `SQL ${(setOpAll[1] ?? 'UNION').toUpperCase()} ALL` }]
    }),
}

// ---------------------------------------------------------------------------------------------
// React identity

// Two-step template detection: the outer pattern is a single unambiguous character class (no
// adjacent unbounded classes — polynomial-ReDoS-safe); the index check runs on the short
// extracted attribute text.
const INDEX_KEY_PATTERN = /key=\{\s*(?:index|idx|i|rowIndex|columnIndex)\s*\}/g
const TEMPLATE_KEY_PATTERN = /key=\{`[^`\n]*`\}/g
const TEMPLATE_INDEX_PATTERN = /\$\{\s*(?:index|idx|i)\s*\}/

const noIndexKey: GuestAuthoringRule = {
  rule: 'no-index-key',
  severity: 'warning',
  summary: 'Never key React lists by array index; key rows by a stable id from the data',
  check: (code) => [
    ...sitesOf(code, INDEX_KEY_PATTERN),
    ...[...code.matchAll(TEMPLATE_KEY_PATTERN)]
      .filter((match) => TEMPLATE_INDEX_PATTERN.test(match[0]))
      .map((match) => ({ index: match.index })),
  ],
}

// ---------------------------------------------------------------------------------------------
// AG Grid

const AG_GRID_TAG = '<AgGridReact'

const agGridMissingGetRowId: GuestAuthoringRule = {
  rule: 'ag-grid-missing-getrowid',
  severity: 'warning',
  summary: 'Every AgGridReact supplies getRowId for deterministic row identity',
  check: (code) => {
    const at = code.indexOf(AG_GRID_TAG)
    return at !== -1 && !code.includes('getRowId') ? [{ index: at }] : []
  },
}

const STOCK_GRID_THEME_PATTERN = /\btheme(?:Quartz|Alpine|Balham)\b/g

const agGridStockTheme: GuestAuthoringRule = {
  rule: 'ag-grid-stock-theme',
  severity: 'error',
  summary:
    'No stock AG Grid theme (themeQuartz/themeAlpine/themeBalham); grids are ARP-themed by ' +
    'default — drop the theme prop, or extend dossGridTheme.withParams({...}) from ' +
    '@doss/guest-runtime',
  check: (code) => sitesOf(code, STOCK_GRID_THEME_PATTERN, (match) => `Stock theme "${match[0]}"`),
}

// The lookbehind keeps hostnames like `www.ag-grid.com` (in a docs-link string) from counting.
const AG_STYLE_OVERRIDE_PATTERN = /(?<![\w.])\.ag-[a-zA-Z][\w-]*|--ag-[a-zA-Z][\w-]*/g

const agGridStyleOverride: GuestAuthoringRule = {
  rule: 'ag-grid-style-override',
  severity: 'error',
  summary:
    'Never restyle AG Grid internals (.ag-* selectors, --ag-* variables); extend ' +
    'dossGridTheme.withParams({...}) or use colDef headerClass/cellClass with guest-owned ' +
    'classnames',
  check: (code) => sitesOf(code, AG_STYLE_OVERRIDE_PATTERN, (match) => `"${match[0]}"`),
}

// ---------------------------------------------------------------------------------------------
// Navigation

// data: is denied only in href position; data: URIs are legitimate for images (CSP img-src).
const HREF_DENIED_SCHEME_PATTERN = /href\s*=\s*(?:\{\s*)?["'`](?:javascript:|data:|\/\/)/gi
const WINDOW_OPEN_PATTERN = /\bwindow\s*\.\s*open\s*\(/g
const FRAME_NAVIGATION_PATTERN =
  /\b(?:window\s*\.\s*)?location\s*\.\s*(?:href\s*=[^=]|assign\s*\(|replace\s*\()/g

const hrefDeniedScheme: GuestAuthoringRule = {
  rule: 'href-denied-scheme',
  severity: 'warning',
  summary:
    'hrefs with javascript:, data:, or protocol-relative // are cancelled in the sandbox; use ' +
    'https:// (opens a new tab), mailto:/tel:, or a root-relative path (target="_top" ' +
    'navigates the Doss app)',
  check: (code) => sitesOf(code, HREF_DENIED_SCHEME_PATTERN),
}

const noWindowOpen: GuestAuthoringRule = {
  rule: 'no-window-open',
  severity: 'warning',
  summary:
    'window.open is sandbox-blocked; render an anchor — absolute http(s) hrefs open a new ' +
    'tab via the host',
  check: (code) => sitesOf(code, WINDOW_OPEN_PATTERN),
}

const noFrameNavigation: GuestAuthoringRule = {
  rule: 'no-frame-navigation',
  severity: 'warning',
  summary:
    'Never assign location.href or call location.assign/replace — it destroys the running ' +
    'app; use anchors (target="_top" navigates the Doss app)',
  check: (code) => sitesOf(code, FRAME_NAVIGATION_PATTERN),
}

// ---------------------------------------------------------------------------------------------
// Loading UX

// The ellipsis text form requires "Loading" with sentence punctuation; the bare JSX-text form
// requires a lowercase continuation, which passes title-case domain headings ("Loading Dock").
const LOADING_TEXT_PATTERN =
  />[^<]{0,40}\bLoading\b[^<]{0,60}?(?:…|\.\.\.)|["'`]Loading\b[^"'`\n]{0,60}?(?:…|\.\.\.)/g
const LOADING_TEXT_BARE_PATTERN = />\s*Loading\s+[a-z][^<]{0,60}</g

const noLoadingText: GuestAuthoringRule = {
  rule: 'no-loading-text',
  severity: 'error',
  summary:
    'Never hand-write loading copy ("Loading…"); pass isLoading to Section/MetricCard, or ' +
    'add className="doss-loadable" with aria-busy on custom widgets (Buttons auto-load on ' +
    'async onClick). Text that merely reads like loading copy is reported as a warning',
  check: (code) => [
    ...sitesOf(code, LOADING_TEXT_PATTERN, () => 'Hand-written loading copy'),
    // Ellipsis forms are already errors above; the bare form is a heuristic, so a warning.
    ...[...code.matchAll(LOADING_TEXT_BARE_PATTERN)]
      .filter((match) => !match[0].includes('…') && !match[0].includes('...'))
      .map((match) => ({
        index: match.index,
        detail: 'Text that reads as loading copy',
        severity: 'warning' as const,
      })),
  ],
}

const LOADING_KEYFRAMES_PATTERN = /@keyframes\s+([a-zA-Z_][\w-]*)/g
const LOADING_ANIMATION_NAME_PATTERN = /pulse|spin|shimmer|skeleton|load/i

const noCustomLoadingAnimation: GuestAuthoringRule = {
  rule: 'no-custom-loading-animation',
  severity: 'error',
  summary:
    'No hand-rolled loading animations (@keyframes pulse/spin/shimmer/skeleton/load); the ' +
    "design system's isLoading / doss-loadable treatment is the only loading affordance",
  check: (code) =>
    [...code.matchAll(LOADING_KEYFRAMES_PATTERN)]
      .filter((match) => LOADING_ANIMATION_NAME_PATTERN.test(match[1] ?? ''))
      .map((match) => ({ index: match.index, detail: `@keyframes ${match[1] ?? ''}` })),
}

const ARIA_BUSY_PATTERN = /aria-busy\s*=/g
// How far a tag opener/closer can sit from an aria-busy attribute (multiline JSX tags).
const ARIA_BUSY_TAG_WINDOW = 500

const ariaBusyWithoutLoadable: GuestAuthoringRule = {
  rule: 'aria-busy-without-loadable',
  severity: 'error',
  summary:
    'aria-busy alone announces busy but paints nothing; pair it with ' +
    'className="doss-loadable" (or use Section/MetricCard isLoading)',
  check: (code) =>
    [...code.matchAll(ARIA_BUSY_PATTERN)]
      .filter((match) => {
        const windowFloor = Math.max(0, match.index - ARIA_BUSY_TAG_WINDOW)
        const tagStart = Math.max(code.lastIndexOf('<', match.index), windowFloor)
        const tagCloseIndex = code.indexOf('>', match.index)
        const tagEnd =
          tagCloseIndex === -1
            ? match.index + ARIA_BUSY_TAG_WINDOW
            : Math.min(tagCloseIndex, match.index + ARIA_BUSY_TAG_WINDOW)
        return !code.slice(tagStart, tagEnd).includes('doss-loadable')
      })
      .map((match) => ({ index: match.index })),
}

const LOADING_CLASSNAME_PATTERN = /className=(?:"[^"\n]*"|'[^'\n]*'|\{`[^`]*`\})/g
const LOADING_CLASSNAME_NAME_PATTERN = /skeleton|shimmer|spinner/i

const noLoadingClassname: GuestAuthoringRule = {
  rule: 'no-loading-classname',
  severity: 'warning',
  summary:
    'No hand-rolled skeleton/shimmer/spinner classnames; use isLoading on Section/MetricCard ' +
    'or doss-loadable + aria-busy',
  check: (code) =>
    [...code.matchAll(LOADING_CLASSNAME_PATTERN)]
      .filter(
        (match) =>
          LOADING_CLASSNAME_NAME_PATTERN.test(match[0]) && !match[0].includes('doss-loadable'),
      )
      .map((match) => ({ index: match.index })),
}

// ---------------------------------------------------------------------------------------------
// AG Grid column alignment. A built-in column type applies the cell and the header alignment
// classes together; a guest classname or cellStyle moves only the cells.

// Two-step again: bounded value/block windows first, then the short extracted text is scanned
// for quoted classnames or a textAlign declaration.
const CELL_CLASS_KEY_PATTERN = /cellClass\s*:/g
// The value runs to the next property key — colDef values are routinely multi-line ternaries
// carrying their own braces, so a character class cannot bound them.
const NEXT_PROPERTY_PATTERN = /\n\s*[a-zA-Z_$][\w$]*\s*:/
const CELL_CLASS_VALUE_WINDOW = 240
// Quoted classnames, plus the static prefix of a template literal (`numeric-cell${…}`).
const QUOTED_STRING_PATTERN = /'([^'\n]{1,80})'|"([^"\n]{1,80})"|`([^`\n$]{1,80})/g
const TEXT_ALIGN_END_PATTERN = /text-align\s*:\s*(right|end)\b/g
// Selector + body of a CSS rule; selectors are routinely grouped across lines.
const CSS_RULE_PATTERN = /([^{}]{0,300})\{([^{}]{0,400})\}/g
const CELL_STYLE_OBJECT_PATTERN = /cellStyle\s*:\s*\{([^}]{0,240})\}/g
const CELL_STYLE_ALIGN_PATTERN = /textAlign\s*:\s*['"](?:right|end)['"]/
// AG's own cell-half class.
const AG_RIGHT_ALIGNED_CELL_CLASS = 'ag-right-aligned-cell'
// Header alignment: a built-in type, or a headerClass carrying AG's header class. A type's value
// is often conditional (`type: numeric ? 'rightAligned' : undefined`), so allow a short lead-in.
const ALIGNMENT_DECLARED_PATTERN =
  /(?<![\w$])type\s*:[^,}\n]{0,60}['"](?:numericColumn|rightAligned)['"]|ag-right-aligned-header/
// A custom header component renders its own markup; AG's header alignment class has nothing to
// act on there.
const CUSTOM_HEADER_PATTERN = /headerComponent\s*:/
// The same alignment-type shape, iterated to find each declaring colDef.
const ALIGNMENT_TYPE_SITE_PATTERN =
  /(?<![\w$])type\s*:[^,}\n]{0,60}['"](?:numericColumn|rightAligned)['"]/g
// How far back a CSS rule's selector can sit from a declaration inside its block.
const CSS_SELECTOR_WINDOW = 300
// Cap on how far the colDef brace-match may scan from one property.
const COL_DEF_SCAN_CAP = 6000

/**
 * The object literal enclosing `index`, found by balancing braces outward. colDefs routinely run
 * dozens of lines (cellClassRules, tooltip getters), so a fixed character window around one
 * property misses its siblings; the cap keeps a malformed source from scanning the whole file.
 */
const enclosingObject = (code: string, index: number, cap: number): string => {
  let depth = 0
  let start = Math.max(0, index - cap)
  for (let i = index; i > start; i -= 1) {
    const ch = code[i]
    if (ch === '}') {
      depth += 1
    } else if (ch === '{') {
      if (depth === 0) {
        start = i
        break
      }
      depth -= 1
    }
  }
  depth = 0
  let end = Math.min(code.length, index + cap)
  for (let i = index; i < end; i += 1) {
    const ch = code[i]
    if (ch === '{') {
      depth += 1
    } else if (ch === '}') {
      if (depth === 0) {
        end = i
        break
      }
      depth -= 1
    }
  }
  return code.slice(start, end)
}

/**
 * True when a CSS selector targets `.className` as a whole class token, so `.num` does not
 * match `.numeric-cell`.
 */
const selectorTargetsClass = (selector: string, className: string): boolean => {
  const needle = `.${className}`
  let from = selector.indexOf(needle)
  while (from !== -1) {
    const after = selector[from + needle.length]
    if (after === undefined || !/[\w-]/.test(after)) {
      return true
    }
    from = selector.indexOf(needle, from + 1)
  }
  return false
}

/**
 * True when some CSS rule targeting `.className` aligns to the inline end. `headerSide` also
 * accepts the flex form, which is how a header label is pushed over.
 */
const classDeclaresAlignment = (code: string, className: string, headerSide: boolean): boolean => {
  for (const match of code.matchAll(CSS_RULE_PATTERN)) {
    const [, selector = '', body = ''] = match
    if (!selectorTargetsClass(selector, className)) {
      continue
    }
    if (/text-align\s*:\s*(?:right|end)\b/.test(body)) {
      return true
    }
    if (headerSide && /justify-content\s*:\s*(?:flex-end|end|right)\b/.test(body)) {
      return true
    }
  }
  return false
}

/**
 * The classnames a colDef property names, read from a bounded window that stops at the next
 * property key.
 */
const propertyClassNames = (scope: string, key: string): string[] => {
  const at = new RegExp(`${key}\\s*:`).exec(scope)
  if (at === null) {
    return []
  }
  const from = at.index + at[0].length
  const raw = scope.slice(from, from + CELL_CLASS_VALUE_WINDOW)
  const boundary = NEXT_PROPERTY_PATTERN.exec(raw)
  const value = boundary === null ? raw : raw.slice(0, boundary.index)
  const names: string[] = []
  for (const quoted of value.matchAll(QUOTED_STRING_PATTERN)) {
    for (const token of (quoted[1] ?? quoted[2] ?? quoted[3] ?? '').split(/\s+/)) {
      if (token !== '') {
        names.push(token)
      }
    }
  }
  return names
}

const alignmentSites = (code: string): GuestLintSite[] => {
  if (!code.includes(AG_GRID_TAG)) {
    return []
  }
  const sites: GuestLintSite[] = []
  // A class is only evidence of a defect where some column applies it WITHOUT an alignment
  // type. Where every use already declares the type the header is aligned and the CSS is
  // merely duplicative — real in the corpus, and not worth failing a working embed over.
  const untypedCellClasses = new Set<string>()
  for (const match of code.matchAll(CELL_CLASS_KEY_PATTERN)) {
    const colDefScope = enclosingObject(code, match.index, COL_DEF_SCAN_CAP)
    if (ALIGNMENT_DECLARED_PATTERN.test(colDefScope) || CUSTOM_HEADER_PATTERN.test(colDefScope)) {
      continue
    }
    const valueStart = match.index + match[0].length
    const raw = code.slice(valueStart, valueStart + CELL_CLASS_VALUE_WINDOW)
    const boundary = NEXT_PROPERTY_PATTERN.exec(raw)
    const value = boundary === null ? raw : raw.slice(0, boundary.index)
    for (const quoted of value.matchAll(QUOTED_STRING_PATTERN)) {
      for (const token of (quoted[1] ?? quoted[2] ?? quoted[3] ?? '').split(/\s+/)) {
        if (token === '') {
          continue
        }
        untypedCellClasses.add(token)
        if (token === AG_RIGHT_ALIGNED_CELL_CLASS) {
          sites.push({
            index: match.index,
            detail: `cellClass "${token}" right-aligns the cells only`,
          })
        }
      }
    }
  }
  for (const match of code.matchAll(TEXT_ALIGN_END_PATTERN)) {
    const braceOpen = code.lastIndexOf('{', match.index)
    if (braceOpen === -1) {
      continue
    }
    // The selector runs from the end of the previous rule (or the template opener) to this
    // block's brace.
    const selectorStart = Math.max(
      code.lastIndexOf('}', braceOpen),
      code.lastIndexOf('{', braceOpen - 1),
      code.lastIndexOf(';', braceOpen),
      code.lastIndexOf('`', braceOpen),
      braceOpen - CSS_SELECTOR_WINDOW,
    )
    const selector = code.slice(Math.max(selectorStart + 1, 0), braceOpen)
    const aligned = [...untypedCellClasses].find((name) => selectorTargetsClass(selector, name))
    if (aligned !== undefined) {
      sites.push({
        index: match.index,
        detail: `Cell alignment in CSS (".${aligned}" is a cellClass)`,
      })
    }
  }
  // The mirror failure: the column DOES declare an alignment type, then overrides one of the
  // two classes that type supplies. AG merges the type first and the colDef over it, so the
  // overridden half silently loses its alignment while the other half keeps it.
  for (const match of code.matchAll(ALIGNMENT_TYPE_SITE_PATTERN)) {
    const scope = enclosingObject(code, match.index, COL_DEF_SCAN_CAP)
    const cellNames = propertyClassNames(scope, 'cellClass')
    if (
      cellNames.length > 0 &&
      !cellNames.some(
        (name) => name === AG_RIGHT_ALIGNED_CELL_CLASS || classDeclaresAlignment(code, name, false),
      )
    ) {
      sites.push({
        index: match.index,
        detail:
          `cellClass "${cellNames.join(' ')}" replaces the alignment type's ` +
          `${AG_RIGHT_ALIGNED_CELL_CLASS}, so the values fall back to the left under a ` +
          'right-aligned header',
      })
    }
    const headerNames = propertyClassNames(scope, 'headerClass')
    if (
      headerNames.length > 0 &&
      !CUSTOM_HEADER_PATTERN.test(scope) &&
      !headerNames.some(
        (name) => name === 'ag-right-aligned-header' || classDeclaresAlignment(code, name, true),
      )
    ) {
      sites.push({
        index: match.index,
        detail:
          `headerClass "${headerNames.join(' ')}" replaces the alignment type's ` +
          'ag-right-aligned-header, so the header falls back to the left over right-aligned values',
      })
    }
  }
  for (const match of code.matchAll(CELL_STYLE_OBJECT_PATTERN)) {
    const colDef = enclosingObject(code, match.index, COL_DEF_SCAN_CAP)
    const headerHandled =
      ALIGNMENT_DECLARED_PATTERN.test(colDef) || CUSTOM_HEADER_PATTERN.test(colDef)
    if (CELL_STYLE_ALIGN_PATTERN.test(match[1] ?? '') && !headerHandled) {
      sites.push({ index: match.index, detail: 'Cell alignment in cellStyle' })
    }
  }
  return sites
}

const agGridCellAlignmentCss: GuestAuthoringRule = {
  rule: 'ag-grid-cell-alignment-css',
  severity: 'error',
  summary:
    "Right-align a column with type: 'numericColumn' (or 'rightAligned'), never through " +
    "cellClass/cellStyle/CSS alone; a colDef cellClass or headerClass replaces the type's, so " +
    'name ag-right-aligned-cell (or ag-right-aligned-header) in it when the column still needs ' +
    'its own class',
  check: alignmentSites,
}

// ---------------------------------------------------------------------------------------------

/** The guest authoring contract, in report order. `doss byofe contract` prints it. */
export const GUEST_AUTHORING_RULES: readonly GuestAuthoringRule[] = [
  noLiteralColor,
  noFontFamilyLiteral,
  sqlLimitWithoutOrderBy,
  sqlNoSetOpAll,
  noIndexKey,
  agGridMissingGetRowId,
  agGridStockTheme,
  agGridStyleOverride,
  hrefDeniedScheme,
  noWindowOpen,
  noFrameNavigation,
  noLoadingText,
  noCustomLoadingAnimation,
  ariaBusyWithoutLoadable,
  noLoadingClassname,
  agGridCellAlignmentCss,
]

/**
 * Lint guest TSX source against the mechanical authoring contract. Returns every finding;
 * callers decide severity policy (byofe check fails only on errors). Does not parse — run
 * `compileGuestSource` first for syntax.
 */
export const lintGuestSource = (source: string): GuestLintFinding[] => {
  const code = blankComments(source)
  return GUEST_AUTHORING_RULES.flatMap((rule) =>
    rule.check(code).map((site) => ({
      rule: rule.rule,
      severity: site.severity ?? rule.severity,
      line: lineOfOffset(code, site.index),
      message: site.detail === undefined ? rule.summary : `${site.detail} — ${rule.summary}`,
    })),
  ).sort((a, b) => a.line - b.line)
}
